import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Shield, Flame, Plus, Trash2, AlertCircle, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useSecurityContext } from '@/contexts/MaintenanceContext';
import { getSystemCategory, getSystemTypeLabel } from '@/lib/systemTypeConstants';
import { usePlantillasCertificados, type TipoCertificado } from '@/hooks/usePlantillasCertificados';
import { useCertificadosInstalacion } from '@/hooks/useCertificadosInstalacion';
import { generarCertificadoPdf } from '@/lib/certificados/certificadoPdf';
import {
  sanitizarTextoContrato,
  GRADOS_UNE_50131,
  type ClausulaCertificado,
} from '@/lib/certificados/plantillasPorDefecto';

interface Props {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  clienteId: string;
  onCreated?: () => void;
}

export function CertificadoFormDialog({ open, onOpenChange, clienteId, onCreated }: Props) {
  const { sistemas, clientes, sedes, elementos } = useSecurityContext();
  const { getPlantilla } = usePlantillasCertificados();
  const { crearCertificado, subirPdf } = useCertificadosInstalacion(clienteId);

  const cliente = clientes.find(c => c.id === clienteId);

  const [tipo, setTipo] = useState<TipoCertificado>('seguridad');
  const [tab, setTab] = useState('datos');
  const [fechaInstalacion, setFechaInstalacion] = useState(new Date().toISOString().slice(0, 10));
  const [sedeId, setSedeId] = useState<string>('none');
  const [referenciaProyecto, setReferenciaProyecto] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [grado, setGrado] = useState<1 | 2 | 3 | 4>(2);
  const [sistemasSel, setSistemasSel] = useState<Set<string>>(new Set());
  const [objeto, setObjeto] = useState('');
  const [clausulas, setClausulas] = useState<ClausulaCertificado[]>([]);
  const [textoLegal, setTextoLegal] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [firmante, setFirmante] = useState('');
  const [generando, setGenerando] = useState(false);

  useEffect(() => {
    if (!open) return;
    const p = getPlantilla(tipo);
    setObjeto(p.objeto);
    setClausulas(p.clausulas.map(c => ({ ...c })));
    setTextoLegal(p.texto_legal);
    setSistemasSel(new Set());
  }, [tipo, open, getPlantilla]);

  useEffect(() => {
    if (open) {
      setTab('datos');
      setFechaInstalacion(new Date().toISOString().slice(0, 10));
      setSedeId('none');
      setReferenciaProyecto('');
      setDescripcion('');
      setGrado(2);
      setObservaciones('');
      setFirmante('');
    }
  }, [open]);

  const sistemasCliente = useMemo(
    () => sistemas.filter(s => s.cliente_id === clienteId && getSystemCategory(s.tipo) === tipo),
    [sistemas, clienteId, tipo],
  );
  const sedesCliente = useMemo(
    () => sedes.filter(s => s.cliente_id === clienteId),
    [sedes, clienteId],
  );

  const hayIntrusion = useMemo(
    () => Array.from(sistemasSel).some(id => sistemas.find(x => x.id === id)?.tipo === 'intrusión'),
    [sistemasSel, sistemas],
  );

  const toggleSistema = (id: string) => {
    setSistemasSel(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const addClausula = () => setClausulas(prev => [...prev, { titulo: '', contenido: '' }]);
  const removeClausula = (i: number) => setClausulas(prev => prev.filter((_, idx) => idx !== i));
  const updateClausula = (i: number, patch: Partial<ClausulaCertificado>) =>
    setClausulas(prev => prev.map((c, idx) => idx === i ? { ...c, ...patch } : c));

  const guardar = async (estadoFinal: 'borrador' | 'emitido') => {
    if (!cliente) return;
    if (sistemasSel.size === 0) {
      toast.error('Debe seleccionar al menos un sistema para incluir en el certificado');
      setTab('alcance');
      return;
    }
    setGenerando(true);
    try {
      const clausulasLimpias = clausulas
        .map(c => ({
          titulo: sanitizarTextoContrato(c.titulo),
          contenido: sanitizarTextoContrato(c.contenido),
        }))
        .filter(c => c.titulo || c.contenido);

      const sedeElegida = sedeId !== 'none' ? sedesCliente.find(s => s.id === sedeId) : null;

      const certificado = await crearCertificado({
        cliente_id: clienteId,
        tipo,
        fecha_instalacion: fechaInstalacion,
        sede_id: sedeId !== 'none' ? sedeId : null,
        sistemas_ids: Array.from(sistemasSel),
        descripcion_instalacion: sanitizarTextoContrato(descripcion),
        referencia_proyecto: sanitizarTextoContrato(referenciaProyecto) || undefined,
        grado_intrusion: hayIntrusion && tipo === 'seguridad' ? grado : null,
        objeto: sanitizarTextoContrato(objeto),
        clausulas: clausulasLimpias,
        texto_legal: sanitizarTextoContrato(textoLegal),
        observaciones: sanitizarTextoContrato(observaciones),
        estado: estadoFinal,
        nombre_firmante_cliente: firmante || undefined,
      });

      const sistemasInfo = Array.from(sistemasSel).map(id => {
        const s = sistemas.find(x => x.id === id);
        const sede = s?.sede_id ? sedes.find(se => se.id === s.sede_id) : null;
        const direccionPartes = sede
          ? [sede.direccion, sede.codigo_postal, sede.poblacion, sede.provincia].filter(Boolean).join(', ')
          : '';
        const elementosSistema = elementos
          .filter(e => e.sistema_id === id)
          .sort((a: any, b: any) => (a.numero_elemento ?? 0) - (b.numero_elemento ?? 0));
        return {
          tipo: s?.tipo || '',
          tipoLabel: s ? getSystemTypeLabel(s.tipo) : '',
          sedeNombre: sede?.nombre || undefined,
          sedeDireccion: direccionPartes || null,
          elementos: elementosSistema.map((e: any) => ({
            nombre: e.nombre,
            tipo_equipo: e.tipo_equipo,
            marca: e.marca,
            modelo: e.modelo,
            mac_serie: e.mac_serie,
            ubicacion: e.area_particion || null,
            zona: e.zona,
            anio_fabricacion: e.anio_fabricacion,
          })),
        };
      });


      const sedeDirParts = sedeElegida
        ? [sedeElegida.direccion, sedeElegida.codigo_postal, sedeElegida.poblacion, sedeElegida.provincia]
            .filter(Boolean).join(', ')
        : '';

      const blob = await generarCertificadoPdf({
        empresaId: cliente.empresa_id!,
        cliente,
        certificado: {
          numero_formateado: certificado.numero_formateado,
          tipo: certificado.tipo,
          fecha_instalacion: certificado.fecha_instalacion,
          descripcion_instalacion: certificado.descripcion_instalacion,
          referencia_proyecto: certificado.referencia_proyecto,
          grado_intrusion: certificado.grado_intrusion,
          sedeNombre: sedeElegida?.nombre || null,
          sedeDireccion: sedeDirParts || null,
          objeto: certificado.objeto,
          clausulas: certificado.clausulas,
          texto_legal: certificado.texto_legal,
          observaciones: certificado.observaciones,
          nombre_firmante_cliente: certificado.nombre_firmante_cliente,
        },
        sistemas: sistemasInfo,
        esBorrador: estadoFinal === 'borrador',
      });

      await subirPdf(certificado, blob);

      toast.success(estadoFinal === 'emitido' ? 'Certificado emitido' : 'Borrador guardado');
      onCreated?.();
      onOpenChange(false);
    } catch (e: any) {
      toast.error(e.message || 'Error al crear el certificado');
    } finally {
      setGenerando(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {tipo === 'seguridad' ? <Shield className="h-5 w-5" /> : <Flame className="h-5 w-5" />}
            Nuevo certificado de instalación
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="datos">1. Datos</TabsTrigger>
            <TabsTrigger value="alcance">2. Alcance</TabsTrigger>
            <TabsTrigger value="clausulas">3. Declaraciones</TabsTrigger>
          </TabsList>

          <TabsContent value="datos" className="space-y-4 pt-4">
            <div>
              <Label>Tipo de certificado</Label>
              <Select value={tipo} onValueChange={(v) => setTipo(v as TipoCertificado)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="seguridad">Seguridad (Intrusión / CCTV / Accesos)</SelectItem>
                  <SelectItem value="pci">Protección Contra Incendios (PCI)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Fecha de instalación</Label>
                <Input type="date" value={fechaInstalacion} onChange={e => setFechaInstalacion(e.target.value)} />
              </div>
              <div>
                <Label>Sede (opcional)</Label>
                <Select value={sedeId} onValueChange={setSedeId}>
                  <SelectTrigger><SelectValue placeholder="Sin sede específica" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin sede específica</SelectItem>
                    {sedesCliente.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Referencia / Nº de proyecto (opcional)</Label>
              <Input value={referenciaProyecto} onChange={e => setReferenciaProyecto(e.target.value)} placeholder="Ej. PROY-2026-014" />
            </div>

            <div>
              <Label>Descripción de la instalación</Label>
              <Textarea
                value={descripcion}
                onChange={e => setDescripcion(e.target.value)}
                rows={3}
                placeholder="Describe brevemente lo instalado: ubicaciones, configuración, particularidades…"
              />
            </div>
          </TabsContent>

          <TabsContent value="alcance" className="space-y-3 pt-4">
            <div className="flex items-start gap-2 rounded-md bg-primary/5 border border-primary/20 p-3 text-sm">
              <ShieldCheck className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <p>
                Selecciona los sistemas instalados que se acreditan en este certificado.
                <strong className="text-destructive"> Es obligatorio seleccionar al menos un sistema.</strong>
              </p>
            </div>
            {sistemasCliente.length === 0 ? (
              <div className="flex items-center gap-2 text-destructive py-8 justify-center">
                <AlertCircle className="h-4 w-4" />
                <span>No hay sistemas de {tipo} dados de alta para este cliente. Dé de alta un sistema antes de emitir el certificado.</span>
              </div>
            ) : (
              <div className="space-y-2">
                {sistemasCliente.map(s => (
                  <label
                    key={s.id}
                    className={`flex items-center gap-3 rounded-md border p-3 cursor-pointer hover:bg-muted/40 ${sistemasSel.has(s.id) ? 'border-primary bg-primary/5' : ''}`}
                  >
                    <Checkbox
                      checked={sistemasSel.has(s.id)}
                      onCheckedChange={() => toggleSistema(s.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium truncate">{getSystemTypeLabel(s.tipo)}</span>
                        <Badge variant="outline" className="text-xs">{tipo.toUpperCase()}</Badge>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}

            {tipo === 'seguridad' && hayIntrusion && (
              <div className="rounded-md border-2 border-primary/30 bg-primary/5 p-4 space-y-3">
                <div className="flex items-start gap-2">
                  <Shield className="h-4 w-4 text-primary mt-0.5" />
                  <div className="flex-1">
                    <Label className="text-sm font-semibold">Grado de seguridad UNE-EN 50131-1</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Obligatorio para instalaciones de intrusión. Selecciona el grado que corresponde a esta instalación.
                    </p>
                  </div>
                </div>
                <Select value={String(grado)} onValueChange={(v) => setGrado(Number(v) as 1 | 2 | 3 | 4)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {([1, 2, 3, 4] as const).map(g => (
                      <SelectItem key={g} value={String(g)}>
                        {GRADOS_UNE_50131[g].titulo}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{GRADOS_UNE_50131[grado].descripcion}</p>
              </div>
            )}

            {sistemasSel.size === 0 && (
              <div className="flex items-center gap-2 text-destructive text-sm font-medium">
                <AlertCircle className="h-4 w-4" />
                <span>No ha seleccionado ningún sistema. Seleccione al menos uno para continuar.</span>
              </div>
            )}
          </TabsContent>

          <TabsContent value="clausulas" className="space-y-4 pt-4">
            <div>
              <Label>Objeto del certificado</Label>
              <Textarea value={objeto} onChange={e => setObjeto(e.target.value)} rows={3} />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Declaraciones / cláusulas (texto plano)</Label>
                <Button type="button" size="sm" variant="outline" onClick={addClausula}>
                  <Plus className="h-3.5 w-3.5 mr-1" /> Añadir
                </Button>
              </div>
              {clausulas.map((c, i) => (
                <div key={i} className="rounded-md border p-3 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Título"
                      value={c.titulo}
                      onChange={e => updateClausula(i, { titulo: e.target.value })}
                    />
                    <Button type="button" variant="ghost" size="icon" onClick={() => removeClausula(i)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  <Textarea
                    placeholder="Contenido (sin HTML — saltos de línea con Enter)"
                    value={c.contenido}
                    onChange={e => updateClausula(i, { contenido: e.target.value })}
                    rows={5}
                  />
                </div>
              ))}
            </div>

            <div>
              <Label>Observaciones</Label>
              <Textarea value={observaciones} onChange={e => setObservaciones(e.target.value)} rows={2} />
            </div>

            <div>
              <Label>Nombre del firmante por el cliente (opcional)</Label>
              <Input value={firmante} onChange={e => setFirmante(e.target.value)} placeholder="Persona que firma" />
            </div>
          </TabsContent>
        </Tabs>

        <div className="rounded-md border bg-muted/30 p-3 text-xs space-y-1.5">
          <p className="font-semibold text-foreground">¿Qué hace cada botón?</p>
          <p><strong>Guardar borrador:</strong> el PDF queda con marca BORRADOR y NO firma nada. Útil para revisar.</p>
          <p><strong>Emitir certificado:</strong> genera el PDF definitivo. Después podrás enviarlo a firmar o subir uno firmado a mano.</p>
        </div>

        <div className="flex flex-wrap justify-end gap-2 pt-2 border-t">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={generando}>Cancelar</Button>
          <Button variant="outline" disabled={generando || sistemasSel.size === 0} onClick={() => guardar('borrador')}>
            {generando ? 'Generando…' : 'Guardar borrador'}
          </Button>
          <Button disabled={generando || sistemasSel.size === 0} onClick={() => guardar('emitido')}>
            {generando ? 'Emitiendo…' : 'Emitir certificado'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
